import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Link as LinkIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNorthDocumentsQuery } from "@/hooks/useNorthDocuments";
import { ListingPerformance } from "@/components/ListingPerformance";
import SocialCampaigns from "@/components/SocialCampaigns";
import { ShowingsPreview } from "@/components/ShowingsPreview";
import { TasksPreview } from "@/components/TasksPreview";
import { CompsPreview } from "@/components/CompsPreview";
import { NeighborhoodDataPreview } from "@/components/NeighborhoodDataPreview";
import { FloorplanTourPreview } from "@/components/FloorplanTourPreview";
import { AgentInsightsSection } from "@/components/AgentInsightsSection";
import { useDefaultLogo } from "@/hooks/useDefaultLogo";
import { logger } from '@/lib/logger';
import { formatMarkdownToSafeHtml } from '@/lib/sanitize';
import NotFound from "@/pages/NotFound";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ConditionalSectionWrapper } from "@/components/ConditionalSectionWrapper";
import { PublicReportLoading } from "@/components/PublicReportLoading";

interface North {
  id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  mls_id: string | null;
  property_type: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  price: number | null;
  image_url: string | null;
  listing_agent: string;
  listing_agent_photo: string | null;
  days_on_zillow: number | null;
  custom_agent_name: string | null;
  custom_agent_headshot_url: string | null;
  custom_logo_url: string | null;
  primary_brand_color: string | null;
  secondary_brand_color: string | null;
  published_at: string | null;
  executive_summary: string | null;
  showings_summary: string | null;
  comps_summary: string | null;
  neighborhood_summary: string | null;
  neighborhood_data: any;
  show_neighborhood_on_report: boolean;
  show_social_on_report: boolean;
  show_showings_on_report: boolean;
  show_floorplan_tour_on_report: boolean;
  show_comps_on_report: boolean;
  show_tasks_on_report: boolean;
  show_agent_insights_on_report: boolean;
  views_count: number | null;
  saves_count: number | null;
  showings_count: number | null;
  price_changes_count: number | null;
  zpid: string | null;
  floorplan_data: any;
  tour_data: any;
}

// UUID format: 8-4-4-4-12 hexadecimal characters
const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Helper function to convert hex color to rgba with opacity
const hexToRgba = (hex: string | null, opacity: number) => {
  if (!hex) return undefined;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return undefined;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Wrapper component for Social Campaigns
function SocialCampaignsWrapper({ northId, address, secondaryColor, showOnReport }: { northId: string; address: string; secondaryColor: string; showOnReport: boolean }) {
  const { data: posts, isLoading } = useQuery({
    queryKey: ["social-campaigns", northId, "v5"],
    queryFn: async () => {
      const isDemo = northId === 'aaaaaaaa-0000-0000-0000-000000000001';
      const targetNorthId = isDemo ? 'd0daa852-5636-45c6-8a29-0d12ba9cf2d1' : northId;

      const { data: junctionData } = await (supabase
        .from("north_social_posts" as any)
        .select("social_post_id")
        .eq("north_id", targetNorthId) as any);

      const { data: directData } = await supabase
        .from("social_media_posts")
        .select("*, account:social_media_accounts(account_name, platform)")
        .eq("north_id", targetNorthId)
        .eq("is_added_to_report", true);

      const seenIds = new Set<string>();
      const allPosts: any[] = [];

      if (junctionData && junctionData.length > 0) {
        const postIds = junctionData.map((item: any) => item.social_post_id);
        const { data: junctionPosts } = await supabase
          .from("social_media_posts")
          .select("*, account:social_media_accounts(account_name, platform)")
          .in("id", postIds)
          .order("posted_at", { ascending: false });
        for (const p of junctionPosts || []) {
          if (!seenIds.has(p.id)) { seenIds.add(p.id); allPosts.push(p); }
        }
      }

      for (const p of directData || []) {
        if (!seenIds.has(p.id)) { seenIds.add(p.id); allPosts.push(p); }
      }

      allPosts.sort((a, b) => new Date(b.posted_at).getTime() - new Date(a.posted_at).getTime());
      return allPosts;
    },
  });

  // Don't render if toggle is off, if loading, or if there's no data
  if (!showOnReport) return null;
  if (isLoading) return null;
  
  const hasData = posts && posts.length > 0;
  if (!hasData) return null;

  return (
    <div
      className="mb-12 p-8 rounded-xl border border-border/50 shadow-sm"
      style={{
        background: `linear-gradient(to bottom right, ${hexToRgba(secondaryColor, 0.05)}, ${hexToRgba(secondaryColor, 0.1)}, hsl(var(--background)))`
      }}
    >
      <SocialCampaigns northId={northId} address={address} />
    </div>
  );
}

// Wrapper component for Showings
function ShowingsWrapper({ northId, showingsSummary, primaryColor, showOnReport }: { northId: string; showingsSummary: string | null; primaryColor: string; showOnReport: boolean }) {
  const { data: showings, isLoading } = useQuery({
    queryKey: ['showings-preview', northId],
    queryFn: async () => {
      const isDemo = northId === 'aaaaaaaa-0000-0000-0000-000000000001';
      if (isDemo) {
        const { data: demoShowings } = await supabase
          .from('demo_showing_events')
          .select('*')
          .eq('demo_report_id', northId)
          .order('scheduled_date', { ascending: false });
        return (demoShowings || []) as any[];
      }

      const { data, error } = await supabase
        .from('showing_events')
        .select('*')
        .eq('north_id', northId)
        .order('scheduled_date', { ascending: false })
        .limit(10);

      if (error) throw error;
      return (data || []) as any[];
    },
  });

  // Don't render if toggle is off, if loading, or if there's no data
  if (!showOnReport) return null;
  if (isLoading) return null;
  
  const hasData = showings && showings.length > 0;
  if (!hasData) return null;

  return (
    <div
      className="mb-12 p-8 rounded-xl border border-border/50 shadow-sm"
      style={{
        background: `linear-gradient(to bottom right, ${hexToRgba(primaryColor, 0.05)}, ${hexToRgba(primaryColor, 0.1)}, hsl(var(--background)))`
      }}
    >
      <ShowingsPreview northId={northId} showingsSummary={showingsSummary} />
    </div>
  );
}

// Wrapper component for Tasks
function TasksWrapper({ northId, primaryColor, showOnReport }: { northId: string; primaryColor: string; showOnReport: boolean }) {
  const { data: tasks, isLoading } = useQuery({
    queryKey: ['tasks-preview', northId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('listing_tasks' as any)
        .select('*')
        .eq('north_id', northId)
        .order('activity_date', { ascending: false }) as any);

      if (error) throw error;
      return (data || []) as any[];
    },
  });

  if (!showOnReport) return null;
  if (isLoading) return null;

  const hasData = tasks && tasks.length > 0;
  if (!hasData) return null;

  return (
    <div
      className="mb-12 p-8 rounded-xl border border-border/50 shadow-sm"
      style={{
        background: `linear-gradient(to bottom right, ${hexToRgba(primaryColor, 0.05)}, ${hexToRgba(primaryColor, 0.1)}, hsl(var(--background)))`
      }}
    >
      <TasksPreview northId={northId} />
    </div>
  );
}

// Documents section for public North report
function DocumentsSection({ northId }: { northId: string }) {
  const { data: documents = [] } = useNorthDocumentsQuery(northId);
  if (documents.length === 0) return null;
  return (
    <div className="mb-12">
      <h3 className="text-lg font-semibold text-foreground mb-4">Documents & Links</h3>
      <div className="space-y-3">
        {documents.map((doc) => {
          const href = doc.link_url ?? doc.public_url;
          const isLink = !!doc.link_url;
          return (
            <div
              key={doc.id}
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white shadow-sm p-4"
            >
              <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500">
                {isLink ? <LinkIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{doc.file_name}</p>
                {doc.file_description && (
                  <p className="text-sm text-slate-400 truncate">{doc.file_description}</p>
                )}
              </div>
              {href && (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 text-sm font-medium px-4 py-2 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Open →
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Wrapper component for Comps
function CompsWrapper({ northId, compsSummary, primaryColor, secondaryColor, accentColor, northProperty }: { northId: string; compsSummary: string | null; primaryColor: string; secondaryColor: string; accentColor: string; northProperty?: { address: string; city: string | null; state: string | null; zip: string | null; price: number | null; bedrooms: number | null; bathrooms: number | null; square_feet: number | null } | null }) {
  const { data: comps, isLoading } = useQuery({
    queryKey: ['comps-preview', northId],
    queryFn: async () => {
      const isDemo = northId === 'aaaaaaaa-0000-0000-0000-000000000001';
      if (isDemo) {
        const { data: demoComps } = await supabase
          .from('demo_comparable_properties')
          .select('*')
          .eq('demo_report_id', northId);
        return (demoComps || []).map(comp => ({
          id: comp.id,
          address: comp.address,
          city: comp.city,
          state: comp.state,
          zip: comp.zip,
          price: comp.price,
          beds: comp.beds,
          baths: comp.baths,
          sqft: comp.sqft,
          status: comp.status,
          image_url: comp.image_url,
          zillow_url: comp.zillow_url,
        })) as any[];
      }

      const { data, error } = await (supabase
        .from('comparable_properties') as any)
        .select('*')
        .eq('north_id', northId)
        .eq('visible', true)
        .order('price', { ascending: false })
        .limit(6);

      if (error) throw error;
      return (data || []).map(comp => ({
        id: comp.id,
        address: comp.address,
        city: comp.city,
        state: comp.state,
        zip: comp.zip,
        price: comp.price,
        beds: comp.beds,
        baths: comp.baths,
        sqft: comp.sqft,
        status: comp.status,
        image_url: comp.image_url,
        zillow_url: comp.zillow_url,
      })) as any[];
    },
  });

  const hasData = comps && comps.length > 0;

  if (isLoading || !hasData) return null;

  return (
    <div
      className="mb-12 p-8 rounded-xl border border-border/50 shadow-sm"
      style={{
        background: `linear-gradient(to bottom right, ${hexToRgba(accentColor, 0.05)}, ${hexToRgba(primaryColor, 0.1)}, hsl(var(--background)))`
      }}
    >
      <CompsPreview
        northId={northId}
        compsSummary={compsSummary}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        northProperty={northProperty}
      />
    </div>
  );
}

export default function PublicNorthReport() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  
  // Hooks must be called before any conditional returns
  const { defaultLogoUrl } = useDefaultLogo();

  // Debug logging
  useEffect(() => {
    console.log('[PublicNorthReport] ID from params:', id);
  }, [id]);

  // Manual state to track data (workaround for React Query state not updating immediately)
  const [manualCompleteData, setManualCompleteData] = useState<any>(null);
  const [manualLoading, setManualLoading] = useState(true);
  const [manualError, setManualError] = useState<any>(null);

  // Single optimized query using the new database function
  const { data: completeData, isLoading, error, isError, isFetching, status } = useQuery({
    queryKey: ["public-north-report-complete", id],
    queryFn: async () => {
      if (!id) return null;
      
      console.log('[PublicNorthReport] 🔄 Fetching complete report data for id:', id);
      
      // Call the optimized database function
      const { data, error: rpcError } = await (supabase.rpc as any)(
        "get_public_report_complete_data",
        { north_id_param: id }
      );

      if (rpcError) {
        console.error('[PublicNorthReport] RPC error:', rpcError);
        throw rpcError;
      }

      if (!data) {
        console.log('[PublicNorthReport] No data returned from RPC');
        return null;
      }

      console.log('[PublicNorthReport] ✅ Complete data fetched successfully', {
        hasReport: !!data.report,
        hasTeam: !!data.team,
        hasShowings: !!data.showings,
        hasComps: !!data.comps,
        reportKeys: data.report ? Object.keys(data.report) : [],
      });
      
      // Normalize report data
      const reportData = data.report;
      if (!reportData) {
        console.log('[PublicNorthReport] ⚠️ No report data in response');
        return null;
      }

      const normalizedReport = {
        ...reportData,
        mls_id: reportData.mls_id || null,
        published_at: reportData.published_at || reportData.created_at || null,
      } as North;
      
      console.log('[PublicNorthReport] 📦 Normalized report:', {
        id: normalizedReport.id,
        address: normalizedReport.address,
      });

      // Populate React Query cache for child components
      const isDemo = normalizedReport.id === 'aaaaaaaa-0000-0000-0000-000000000001';
      
      // Cache showings
      if (data.showings) {
        queryClient.setQueryData(['showings-preview', normalizedReport.id], data.showings);
      }

      // Cache comps (map to expected format)
      if (data.comps) {
        const mappedComps = data.comps.map((comp: any) => ({
          id: comp.id,
          address: comp.address,
          city: comp.city,
          state: comp.state,
          zip: comp.zip,
          price: comp.price,
          beds: comp.beds || comp.bedrooms,
          baths: comp.baths || comp.bathrooms,
          sqft: comp.sqft || comp.square_feet,
          status: comp.status,
          image_url: comp.image_url,
          zillow_url: comp.zillow_url,
        }));
        queryClient.setQueryData(['comps-preview', normalizedReport.id], mappedComps);
      }

      // Cache social campaigns
      if (data.social_posts) {
        queryClient.setQueryData(["social-campaigns", normalizedReport.id, "v4"], data.social_posts);
      }

      // Cache agent insights
      if (data.agent_insights) {
        queryClient.setQueryData(['agent-insights-section', normalizedReport.id], data.agent_insights);
      }

      // Cache team data
      if (data.team) {
        queryClient.setQueryData(["public-report-team-data", normalizedReport.id], data.team);
      }

      // Preload Mapbox token for CompsPreview map (if not already cached)
      if (!queryClient.getQueryData(["mapbox-token", "public"])) {
        supabase.functions.invoke('get-mapbox-token', { body: { public: true } })
          .then(({ data, error }) => {
            if (!error && data?.token) {
              queryClient.setQueryData(["mapbox-token", "public"], data.token);
            }
          })
          .catch(err => console.error('[PublicNorthReport] Error preloading Mapbox token:', err));
      }

      const result = {
        report: normalizedReport,
        team: data.team || { team_name: null, company_logo_url: null },
        showings: data.showings || [],
        comps: data.comps || [],
        socialPosts: data.social_posts || [],
        agentInsights: data.agent_insights || null,
        listingStats: data.listing_stats || null,
      };

      // Update manual state immediately when data is available
      setManualCompleteData(result);
      setManualLoading(false);
      setManualError(null);

      return result;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 3, // 3 minutes - moderate caching
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnMount: false, // Use cache if available - don't refetch unnecessarily
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Update manual state when React Query state updates (fallback)
  useEffect(() => {
    if (completeData && !manualCompleteData) {
      setManualCompleteData(completeData);
      setManualLoading(false);
    }
    if (isError && error) {
      setManualError(error);
      setManualLoading(false);
    }
  }, [completeData, isError, error, manualCompleteData]);

  // Use manual state as primary source (updates immediately when query completes)
  const effectiveCompleteData = manualCompleteData || completeData;
  
  const companyLogoUrl = effectiveCompleteData?.team?.company_logo_url || null;

  // Extract data from complete response
  const north = effectiveCompleteData?.report || null;
  const teamData = effectiveCompleteData?.team || null;

  // Debug logging
  useEffect(() => {
    console.log('[PublicNorthReport] 🔍 Current state:', {
      isLoading,
      isFetching,
      status,
      isError,
      manualLoading,
      hasManualData: !!manualCompleteData,
      hasCompleteData: !!completeData,
      hasEffectiveData: !!effectiveCompleteData,
      hasNorth: !!north,
      hasTeamData: !!teamData,
      northId: north?.id,
    });
  }, [isLoading, isFetching, status, isError, manualLoading, manualCompleteData, completeData, effectiveCompleteData, north, teamData]);

  // Classify referrer hostname into a source key
  const classifyReferrer = (referrer: string): string => {
    if (!referrer) return 'direct';
    try {
      const hostname = new URL(referrer).hostname.replace(/^www\./, '');
      const map: Record<string, string> = {
        'facebook.com': 'facebook', 'm.facebook.com': 'facebook', 'l.facebook.com': 'facebook',
        'instagram.com': 'instagram', 'l.instagram.com': 'instagram',
        'youtube.com': 'youtube', 'm.youtube.com': 'youtube', 'youtu.be': 'youtube',
        'linkedin.com': 'linkedin', 'lnkd.in': 'linkedin',
        'tiktok.com': 'tiktok',
        'twitter.com': 'twitter', 'x.com': 'twitter', 't.co': 'twitter',
        'zillow.com': 'zillow',
        'realtor.com': 'realtor',
      };
      return map[hostname] ?? 'other';
    } catch {
      return 'other';
    }
  };

  // Defer view tracking to run after render
  useEffect(() => {
    if (!north || isLoading) return;

    // Only track views for published reports (not demo reports)
    const isDemo = id === 'aaaaaaaa-0000-0000-0000-000000000001';
    const isValidUUID = id && uuidRegex.test(id);

    if (!isDemo && isValidUUID) {
      const source = classifyReferrer(document.referrer);

      // Use requestIdleCallback or setTimeout to defer view tracking
      const trackView = async () => {
        try {
          const { error: incrementError } = await (supabase.rpc as any)('increment_public_url_views_with_source', {
            north_id_param: id,
            source_param: source,
          });

          if (incrementError) {
            logger.error('Error incrementing public URL views:', incrementError);
          }
        } catch (error) {
          logger.error('Error tracking view:', error);
          // Don't fail the page load if view tracking fails
        }
      };

      // Defer to next tick to avoid blocking render
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        requestIdleCallback(trackView, { timeout: 2000 });
      } else {
        setTimeout(trackView, 100);
      }
    }
  }, [north, id, isLoading]);
  
  // Handle loading state - use manual state as primary source
  const hasData = !!(north && effectiveCompleteData);
  const effectiveLoading = manualLoading && !hasData;
  
  if (effectiveLoading) {
    console.log('[PublicNorthReport] ⏳ Still loading, showing loading screen', {
      manualLoading,
      isLoading,
      isFetching,
      status,
      hasNorth: !!north,
      hasManualData: !!manualCompleteData,
      hasCompleteData: !!completeData,
      hasData,
    });
    return <PublicReportLoading companyLogoUrl={companyLogoUrl} />;
  }
  
  // If we have data, render it
  if (hasData) {
    console.log('[PublicNorthReport] ✅ Have data, rendering report', {
      northId: north?.id,
      status,
      isLoading,
      manualLoading,
      fromManual: !!manualCompleteData,
    });
    // Continue to render below
  } else if (!effectiveLoading && !hasData) {
    // Loading completed but no data
    console.log('[PublicNorthReport] ❌ Loading completed but no data found');
    if (id && id !== 'aaaaaaaa-0000-0000-0000-000000000001' && !uuidRegex.test(id)) {
      return <NotFound />;
    }
    return <NotFound />;
  }

  // Handle error state
  if (isError) {
    console.log('[PublicNorthReport] ❌ Error occurred:', error);
    // Check if invalid ID format
    if (id && id !== 'aaaaaaaa-0000-0000-0000-000000000001' && !uuidRegex.test(id)) {
      return <NotFound />;
    }
    return <NotFound />;
  }

  // If no data after loading completes, show 404
  if (!isLoading && !north) {
    console.log('[PublicNorthReport] ❌ No data found after loading completed');
    if (id && id !== 'aaaaaaaa-0000-0000-0000-000000000001' && !uuidRegex.test(id)) {
      return <NotFound />;
    }
    return <NotFound />;
  }

  // If we have data, render the report
  if (!north) {
    console.log('[PublicNorthReport] ⚠️ No north data available');
    return <PublicReportLoading companyLogoUrl={companyLogoUrl} />;
  }

  const fullAddress = `${north.address}, ${north.city}, ${north.state} ${north.zip}`;
  const agentName = north.custom_agent_name || north.listing_agent || teamData?.team_name || "";
  // Logo priority: team branding logo -> custom logo -> platform settings -> North logo fallback
  const logoUrl = teamData?.company_logo_url || north.custom_logo_url || defaultLogoUrl;
  const agentHeadshot = north.custom_agent_headshot_url || north.listing_agent_photo;

  // Get brand colors or fallback to theme colors
  const primaryColor = north.primary_brand_color || '#0c0f24';
  const secondaryColor = north.secondary_brand_color || '#1b489b';
  const accentColor = north.primary_brand_color || '#c084fc'; // Use primary as accent fallback

  return (
    <div className="min-h-screen bg-background">
      <div className="flex items-start justify-center px-4 py-8">
        <Card className="w-full max-w-5xl shadow-lg">
          <CardContent className="p-0">
            {/* Logo Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
              <img src={logoUrl} alt="Logo" className="h-12 md:h-16" />
              
              <div className="flex items-center gap-2 md:gap-3">
                <div className="text-right md:text-left md:order-first">
                  <p className="text-xs md:text-xs text-muted-foreground uppercase tracking-wider">Listed By</p>
                  <p className="font-semibold text-sm md:text-base text-foreground">{agentName}</p>
                </div>
                {agentHeadshot ? (
                  <img 
                    src={agentHeadshot} 
                    alt={agentName}
                    className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-xs md:text-base">
                    {agentName.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>
                )}
              </div>
            </div>

            {/* Property Image */}
            {north.image_url && (
              <div className="relative">
                <img 
                  src={north.image_url} 
                  alt={fullAddress}
                  className="w-full h-[400px] object-cover"
                />
              </div>
            )}

            {/* Content */}
            <div className="p-8">
              {/* Property Header */}
              <div className="mb-8">
                <div className="flex items-start justify-between mb-3">
                  <h1 className="text-xl md:text-3xl font-bold text-foreground">
                    {fullAddress}
                  </h1>
                  {north.price && (
                    <p className="text-xl md:text-3xl font-bold text-foreground ml-8 whitespace-nowrap">
                      ${north.price.toLocaleString()}
                    </p>
                  )}
                </div>
                
                {/* Property Stats in single line */}
                {(north.bedrooms || north.bathrooms || north.square_feet) && (
                  <div className="flex items-center gap-3 text-base text-muted-foreground">
                    {north.bedrooms && (
                      <>
                        <span>{north.bedrooms} beds</span>
                        {(north.bathrooms || north.square_feet) && <span>•</span>}
                      </>
                    )}
                    {north.bathrooms && (
                      <>
                        <span>{north.bathrooms} baths</span>
                        {north.square_feet && <span>•</span>}
                      </>
                    )}
                    {north.square_feet && (
                      <span>{north.square_feet.toLocaleString()} sqft</span>
                    )}
                  </div>
                )}
              </div>

              {/* The Big Picture */}
              <div className="mb-12 p-8 rounded-xl bg-gradient-to-br from-muted/5 via-muted/10 to-background border border-border/50 shadow-sm">
                <h3 className="text-sm font-semibold tracking-wider text-foreground mb-4">
                  THE BIG PICTURE
                </h3>
                {north.executive_summary ? (
                  <div
                    className="text-base text-muted-foreground leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: formatMarkdownToSafeHtml(north.executive_summary)
                    }}
                  />
                ) : (
                  <p className="text-base text-muted-foreground">No executive summary available.</p>
                )}
              </div>

              {/* Listing Performance */}
              <div className="mb-12">
                <ListingPerformance
                  northId={north.id}
                  zillowViews={north.views_count || 1249}
                  primaryColor={north.primary_brand_color || '#0c0f24'}
                  secondaryColor={north.secondary_brand_color || '#1b489b'}
                  preloadedStats={(() => {
                    // Calculate PlatformStats from listingStats if available
                    if (effectiveCompleteData?.listingStats) {
                      const ls = effectiveCompleteData.listingStats;
                      const socialPosts = ls.social_posts || [];
                      
                      console.log('[PublicNorthReport] 📊 Calculating preloaded stats for ListingPerformance', {
                        hasListingStats: !!ls,
                        socialPostsCount: socialPosts.length,
                        realtorViews: ls.realtor_views,
                        zillowSaves: ls.zillow_saves,
                      });
                      
                      // Calculate platform stats from social posts
                      const platformStats = socialPosts.reduce((acc: any, post: any) => {
                        const platform = post.platform?.toLowerCase();
                        if (platform && platform in acc) {
                          const engagement = post.engagement || 0;
                          const reach = post.reach || 0;
                          const shares = post.shares_count || 0;
                          const totalEngagement = engagement + reach + shares;
                          acc[platform] += totalEngagement;
                          
                          if (platform === 'youtube') {
                            acc.youtubeViews += post.views_count || 0;
                          }
                        }
                        return acc;
                      }, {
                        zillow: ls.zillow_views || north.views_count || 0,
                        zillowSaves: ls.zillow_saves || 0,
                        realtor: ls.realtor_views || 0,
                        realtorSaves: ls.realtor_saves || 0,
                        homes: ls.homes_views || 0,
                        homesSaves: ls.homes_saves || 0,
                        redfin: ls.redfin_views || 0,
                        redfinSaves: ls.redfin_saves || 0,
                        trulia: ls.trulia_views || 0,
                        truliaSaves: ls.trulia_saves || 0,
                        mls: ls.mls_views || 0,
                        mlsSaves: ls.mls_saves || 0,
                        instagram: 0,
                        facebook: 0,
                        youtube: 0,
                        youtubeViews: 0,
                      });
                      
                      console.log('[PublicNorthReport] ✅ Preloaded stats calculated:', platformStats);
                      return platformStats;
                    }
                    console.log('[PublicNorthReport] ⚠️ No listingStats available for preloading');
                    return null;
                  })()}
                />
              </div>

              {/* Social Campaigns - Only show if toggle is on AND there's data */}
              <SocialCampaignsWrapper
                northId={north.id}
                address={north.address}
                secondaryColor={secondaryColor}
                showOnReport={north.show_social_on_report === true}
              />

              {/* Showings - Only show if toggle is on AND there's data */}
              <ShowingsWrapper
                northId={north.id}
                showingsSummary={north.showings_summary}
                primaryColor={primaryColor}
                showOnReport={north.show_showings_on_report === true}
              />

              {/* Tasks - Only show if toggle is on AND there's data */}
              <TasksWrapper
                northId={north.id}
                primaryColor={primaryColor}
                showOnReport={north.show_tasks_on_report !== false}
              />

              {/* Floorplan & Tour */}
              {north.show_floorplan_tour_on_report === true && (
                <FloorplanTourPreview
                  northId={north.id}
                  floorplanData={north.floorplan_data}
                  tourData={north.tour_data}
                  propertyImage={north.image_url || undefined}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                />
              )}

              {/* Comps Section */}
              {north.show_comps_on_report === true && (
                <CompsWrapper
                  northId={north.id}
                  compsSummary={north.comps_summary}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  accentColor={accentColor}
                  northProperty={{
                    address: north.address,
                    city: north.city,
                    state: north.state,
                    zip: north.zip,
                    price: north.price,
                    bedrooms: north.bedrooms,
                    bathrooms: north.bathrooms,
                    square_feet: north.square_feet,
                  }}
                />
              )}

              {/* Neighborhood Data Section */}
              {north.show_neighborhood_on_report === true && north.neighborhood_data && (
                <div
                  className="mb-12 p-8 rounded-xl border border-border/50 shadow-sm"
                  style={{
                    background: `linear-gradient(to bottom right, rgba(219, 234, 254, 0.5), rgba(191, 219, 254, 0.3), hsl(var(--background)))`
                  }}
                >
                  <NeighborhoodDataPreview
                    neighborhoodData={north.neighborhood_data}
                    neighborhoodSummary={north.neighborhood_summary}
                    forceMobileLayout={false}
                  />
                </div>
              )}

              {/* Agent Insights Section */}
              {north.show_agent_insights_on_report === true && (
                <AgentInsightsSection
                  northId={north.id}
                  primaryColor={north.primary_brand_color || undefined}
                  forceMobileLayout={false}
                />
              )}

              {/* Documents & Links Section */}
              <DocumentsSection northId={north.id} />

              {/* Footer */}
              <div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">Powered with</span>
                  <img src={defaultLogoUrl} alt="North Reports Logo" className="h-8" />
                </div>
                <div className="text-xs text-muted-foreground">
                  Published: {north.published_at ? new Date(north.published_at).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }) : 'Recently'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
